import { useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type ColumnFiltersState,
} from '@tanstack/react-table'
import type { Venue, QualityScore } from '@brent/shared/types'
import { useAuth } from '@/contexts/AuthContext'
import { useUpdateVenue } from '@/hooks/useVenues'
import { useToast } from '@/hooks/use-toast'
import { QualityDropdown } from '@/components/shared/QualityDropdown'
import { WikidataLink, WikidataSearchLink } from '@/components/shared/WikidataLink'
import { InlineEdit } from '@/components/shared/InlineEdit'
import { Button } from '@brent/core-ui/ui'
import { Input } from '@brent/core-ui/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@brent/core-ui/ui'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@brent/core-ui/ui'
import { ArrowUpDown, ChevronLeft, ChevronRight } from 'lucide-react'

export type VenueFilter = 'all' | 'verified' | 'needs_review' | 'pending'

interface VenuesTableProps {
  venues: Venue[]
  filter: VenueFilter
  onFilterChange: (filter: VenueFilter) => void
}

export function VenuesTable({ venues, filter, onFilterChange }: VenuesTableProps) {
  const navigate = useNavigate()
  const { canEdit } = useAuth()
  const updateVenue = useUpdateVenue()
  const { toast } = useToast()
  const [sorting, setSorting] = useState<SortingState>([{ id: 'name', desc: false }])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])

  const filteredVenues = useMemo(
    () =>
      venues.filter((v) => {
        switch (filter) {
          case 'verified':
            return v.quality_score === 'verified'
          case 'needs_review':
            return v.quality_score === 'needs_review'
          case 'pending':
            return v.quality_score === 'pending'
          default:
            return true
        }
      }),
    [venues, filter],
  )

  const handleQualityChange = useCallback(
    async (venue: Venue, qualityScore: QualityScore) => {
      try {
        await updateVenue.mutateAsync({
          id: venue.id,
          input: { quality_score: qualityScore },
        })
        toast({ title: 'Updated', description: `Quality set to ${qualityScore}` })
      } catch (error) {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: error instanceof Error ? error.message : 'Failed to update quality',
        })
      }
    },
    [updateVenue, toast],
  )

  const handleQidSave = useCallback(
    async (venue: Venue, qid: string) => {
      try {
        const result = await updateVenue.mutateAsync({
          id: venue.id,
          input: { wikidata_id: qid },
        })
        if (result.enriched) {
          toast({
            title: 'Enriched',
            description: `Matched to ${result.enriched.label}`,
          })
        } else if (result.cleared) {
          toast({ title: 'Cleared', description: 'Wikidata ID removed' })
        }
      } catch (error) {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: error instanceof Error ? error.message : 'Failed to update QID',
        })
      }
    },
    [updateVenue, toast],
  )

  const columns: ColumnDef<Venue>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Name
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => <span className="font-medium">{row.getValue('name')}</span>,
      },
      {
        accessorKey: 'wikidata_id',
        header: 'QID',
        cell: ({ row }) => {
          const venue = row.original
          if (canEdit) {
            return (
              <InlineEdit
                value={venue.wikidata_id || ''}
                onSave={(qid) => handleQidSave(venue, qid)}
                placeholder="Add QID"
              />
            )
          }
          return <WikidataLink qid={venue.wikidata_id} />
        },
      },
      {
        accessorKey: 'wikidata_label',
        header: 'Wikidata Label',
        cell: ({ row }) => {
          const venue = row.original
          if (venue.wikidata_label) {
            return <WikidataLink qid={venue.wikidata_id} label={venue.wikidata_label} />
          }
          if (venue.name && !venue.wikidata_id) {
            return <WikidataSearchLink text={venue.name} />
          }
          return <span className="text-muted-foreground">-</span>
        },
      },
      {
        accessorKey: 'notes',
        header: 'Notes',
        cell: ({ row }) => {
          const notes = row.getValue('notes') as string | null
          return notes ? (
            <span className="text-sm max-w-[300px] truncate block">{notes}</span>
          ) : (
            <span className="text-muted-foreground">-</span>
          )
        },
      },
      {
        accessorKey: 'quality_score',
        header: 'Status',
        cell: ({ row }) => {
          const venue = row.original
          if (canEdit) {
            return (
              <QualityDropdown
                value={venue.quality_score}
                onChange={(qs) => handleQualityChange(venue, qs)}
                disabled={updateVenue.isPending}
              />
            )
          }
          return <span className="text-sm capitalize">{venue.quality_score.replace('_', ' ')}</span>
        },
      },
    ],
    [canEdit, handleQidSave, handleQualityChange, updateVenue.isPending],
  )

  const table = useReactTable({
    data: filteredVenues,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    state: { sorting, columnFilters },
    initialState: {
      pagination: { pageSize: 25 },
    },
  })

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <Input
          placeholder="Search venues..."
          value={(table.getColumn('name')?.getFilterValue() as string) ?? ''}
          onChange={(e) => table.getColumn('name')?.setFilterValue(e.target.value)}
          className="max-w-sm"
        />
        <Select value={filter} onValueChange={(v) => onFilterChange(v as VenueFilter)}>
          <SelectTrigger className="w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="verified">Verified</SelectItem>
            <SelectItem value="needs_review">Needs Review</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
          </SelectContent>
        </Select>
        <div className="sm:ml-auto text-sm text-muted-foreground">
          {table.getFilteredRowModel().rows.length} of {filteredVenues.length} venues
        </div>
      </div>

      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/venues/${row.original.id}`)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No venues found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={String(table.getState().pagination.pageSize)}
            onValueChange={(v) => table.setPageSize(Number(v))}
          >
            <SelectTrigger className="h-8 w-[80px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[10, 25, 50, 100].map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}
